'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { useIsSpaApp, useResolveNavigationTarget, useRouter, useShouldOpenNonSpaRouteInNewTab, } from '#pplx/frontend/router/provider.js';
import React, { useCallback, useEffect, useState } from 'react';
/**
 * This component is used to handle SPA routes.
 * It will prevent the default behavior of the link and instead push the new state to the history.
 * This is useful for SEO and performance.
 *
 * @param href - The href of the link.
 * @param props - The props of the link.
 * @returns A link component that handles SPA routes.
 */
export const Link = ({ href, onClick, hardNav, target: rawTarget, ref, ...props }) => {
    const router = useRouter();
    const shouldOpenNonSpaRouteInNewTab = useShouldOpenNonSpaRouteInNewTab();
    const resolveNavigationTarget = useResolveNavigationTarget();
    const isSpaApp = useIsSpaApp();
    const [hasMounted, setHasMounted] = useState(false);
    useEffect(() => {
        setHasMounted(true);
    }, []);
    const resolvedHref = hasMounted && href && !href.startsWith('#') ? resolveNavigationTarget(href) : null;
    const isSpaHref = resolvedHref?.isTargetSpaRoute ?? false;
    const browserHref = resolvedHref?.isTargetSpaRoute ? resolvedHref.absolutePath : href;
    // We're being loose here since href technically can be a full URL
    // This would make target becomes _blank when we're in strict SPA mode anw
    const target = hasMounted &&
        isSpaApp &&
        href &&
        !href.startsWith('#') &&
        !isSpaHref &&
        shouldOpenNonSpaRouteInNewTab
        ? '_blank'
        : rawTarget;
    const handleClick = useCallback((e) => {
        // Attached click handlers get precedence over internal wrapper
        onClick?.(e);
        if (e.defaultPrevented) {
            return;
        }
        // Hash-only hrefs are in-page anchors — let the browser handle them
        // natively instead of routing, which would navigate away.
        if (href?.startsWith('#')) {
            return;
        }
        const isMetaKey = e.ctrlKey || e.metaKey || e.shiftKey || e.altKey;
        if (
        // if not opening in new tab or window, or opening a right click menu, or saving to download, or mousewheel clicking...
        !isMetaKey &&
            !(e.button === 1) &&
            href &&
            target !== '_blank') {
            // Prevent native anchor navigation — using client-side routing
            e.preventDefault();
            if (hardNav) {
                // ast-grep-ignore: no-window-location-href
                window.location.href = browserHref ?? href;
            }
            else {
                router.push(href);
            }
        }
    }, [browserHref, hardNav, href, onClick, router, target]);
    return _jsx("a", { ...props, ref: ref, target: target, href: browserHref, onClick: handleClick });
};
//# sourceMappingURL=Link.js.map